import * as React from 'react';
import type { IListPageActions } from './list-page.types';

/**
 * Wires the parent-supplied `actionsRef` to a stable `IListPageActions` handle. The returned
 * handle's identity does not change across renders — every method internally reads the
 * latest `actions` argument from a ref so callers always invoke up-to-date data-layer
 * functions even if React Query's `refetch` reference rotates.
 *
 * The ref is cleared on unmount to prevent late callers (e.g. a setTimeout that survives a
 * page transition) from invoking a stale handle into a torn-down React-Query observer.
 */
export const useListPageActionsHandle = (
	actions: IListPageActions,
	ref?: React.MutableRefObject<IListPageActions | null>
): void => {
	const latestActionsRef = React.useRef(actions);
	latestActionsRef.current = actions;

	const stableHandle = React.useMemo<IListPageActions>(
		() => ({
			refetch: () => latestActionsRef.current.refetch(),
		}),
		[]
	);

	React.useEffect(() => {
		if (!ref) return undefined;
		ref.current = stableHandle;
		return () => {
			if (ref.current === stableHandle) ref.current = null;
		};
	}, [ref, stableHandle]);
};
